import type {
    DiscoveryGetFileUploadUrlResponse,
    ContentDetailsData,
} from "@amzn/ads-discovery-theme-service-typescript-client";
import type {
    Content,
    SalesforceAssociation,
} from "@amzn/ads-measurement-plm-service-type-script-client";
import {
    ContentAction,
    ContentType,
    SalesforceObjectType,
} from "@amzn/ads-measurement-plm-service-type-script-client";
import type {
    AsyncState,
    UseAsyncActions,
    UseAsyncMeta,
} from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { useStore } from "zustand";

import { getClientId } from "../../api/adsApi/adsApi.config";
import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import { getPlmApiClient } from "../../api/plmApi/plmApi";
import { authStore } from "../../store/auth";
import { FeatureFlags, FeatureFlagsStore } from "../../store/featureFlags";
import { insightDetailsStore } from "../../store/insightDetails";
import type { SfObject } from "../../store/insightLinkage";
import { notificationStore, NotificationType } from "../../store/notification";
import { ContentPreviewType } from "../../types/content";
import {
    resolveSfObjectId,
    resolveSfObjectType,
} from "../../utils/sfObject.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export interface PitchFileUploadActionResponse {}

interface BasePitchFileUploadActionRequest {
    insightTitle: string;
    notes: string;
    accountOpportunityId: string;
    sfObject: SfObject;
    sfAccountId: string;
}

export interface PitchFileUploadActionRequestUpload
    extends BasePitchFileUploadActionRequest {
    file: File;
    contentId?: never;
}

export interface PitchFileUploadActionRequestEdit
    extends BasePitchFileUploadActionRequest {
    file?: File;
    contentId: string;
}

export type PitchFileUploadActionRequest =
    | PitchFileUploadActionRequestUpload
    | PitchFileUploadActionRequestEdit;

export function usePitchFileUploadAction(
    props: PitchFileUploadActionRequest,
): [
    AsyncState<PitchFileUploadActionResponse>,
    UseAsyncActions<PitchFileUploadActionResponse>,
    UseAsyncMeta<PitchFileUploadActionResponse>,
] {
    const {
        insightTitle,
        notes,
        accountOpportunityId,
        sfAccountId,
        sfObject,
        file,
        contentId,
    } = props;

    const [fileUploadUrlState, fileUploadUrlAction] =
        useAsync<DiscoveryGetFileUploadUrlResponse>(async () => {
            const response = await (
                await getDiscoveryApiClient()
            ).getFileUploadUrl(file!.name, contentId);
            return response;
        });

    const [uploadFileState, uploadFileAction] = useAsync(
        async (uploadUrl: string, file: File) => {
            const response = await fetch(uploadUrl, {
                method: "PUT",
                headers: {
                    "Content-Type": file.type,
                },
                body: file,
            });
            return response;
        },
    );

    const [uploadContentDetailsState, putContentDetailsAction] = useAsync(
        async (
            contentId: string,
            contentDetails: ContentDetailsData,
            accountOpportunities: string[],
        ) => {
            const response = await (
                await getDiscoveryApiClient()
            ).putContentDetails(
                contentId,
                contentDetails,
                accountOpportunities,
            );
            return response;
        },
    );

    // call getContentDetails to retrieve content metadata
    const [getContentDetailsState, getContentDetailsAction] = useAsync(
        async (contentId: string) => {
            const response = await (
                await getDiscoveryApiClient()
            ).getContentDetails(contentId, ContentPreviewType.ORIGINAL, false);
            return response;
        },
    );

    const [createActivityAssociationState, createActivityAssociationAction] =
        useAsync(
            async (
                contentMetadata: Content,
                salesforceAssociations: SalesforceAssociation[],
            ) => {
                const response = await (
                    await getPlmApiClient()
                ).createContentSfAssociation(
                    contentMetadata,
                    ContentAction.UPLOAD,
                    salesforceAssociations,
                );
                return response;
            },
            {},
        );

    // New action for PLM via AdsBlink BFF
    const [createAssociationViaBlinkState, createAssociationViaBlinkAction] =
        useAsync(
            async (
                contentMetadata: Content,
                salesforceAssociations: SalesforceAssociation[],
            ) => {
                const response = await (
                    await getBlinkApiClientV2()
                ).createContentSfAssociationViaBlink({
                    userAlias: authStore.getState().username,
                    clientId: getClientId(),
                    content: contentMetadata,
                    contentAction: ContentAction.UPLOAD,
                    salesforceAssociations,
                });
                return response;
            },
            {},
        );

    const flags = useStore(FeatureFlagsStore);

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const [resetInsightDetails] = useStore(insightDetailsStore, (state) => [
        state.resetState,
    ]);

    useAsyncErrorStatusNotification(fileUploadUrlState);
    useAsyncErrorStatusNotification(uploadFileState);
    useAsyncErrorStatusNotification(uploadContentDetailsState);
    useAsyncErrorStatusNotification(getContentDetailsState);
    useAsyncErrorStatusNotification(createActivityAssociationState);

    return useAsync(async () => {
        upsertNotification(
            "Uploading insight deck",
            NotificationType.LOADING,
            insightTitle,
        );

        let uploadContentId = contentId;

        // If we have a file, then upload it first
        if (file) {
            const uploadResponse = await fileUploadUrlAction.execute(file.name);
            uploadContentId = uploadResponse.contentId;

            await uploadFileAction.execute(uploadResponse.uploadUrl!, file);
        }

        const isActivityAssociated =
            resolveSfObjectType(sfObject) === SalesforceObjectType.ACTIVITY;

        const accountOpportunities: string[] = isActivityAssociated
            ? []
            : [accountOpportunityId];

        // Phase 3: Skip writing to Theme Service (only write to PLM via AdsBlink BFF)
        // Phase 1 & 2 & Legacy: Write to Theme Service
        if (!flags[FeatureFlags.PLM_WRITE_AND_READ_PLM]) {
            await putContentDetailsAction.execute(
                uploadContentId!,
                {
                    contentTitle: insightTitle,
                    contentType: "PITCH",
                    visibility: "PRIVATE",
                    contentNote: notes,
                },
                accountOpportunities,
            );
        } else {
            // Phase 3: Still need to write content details, but without account/opportunity associations
            await putContentDetailsAction.execute(
                uploadContentId!,
                {
                    contentTitle: insightTitle,
                    contentType: "PITCH",
                    visibility: "PRIVATE",
                    contentNote: notes,
                },
                [], // Empty array - no account/opportunity associations to Theme Service
            );
        }

        const getContentDetailsResponse = await getContentDetailsAction.execute(
            uploadContentId!,
        );

        const contentMetadata: Content = {
            contentId: uploadContentId!,
            contentType: ContentType.PITCHED_CONTENT,
            contentName: getContentDetailsResponse.content!.contentTitle,
            contentURL: getContentDetailsResponse.downloadUrl,
            contentExpiryDate:
                getContentDetailsResponse.content!.autoArchiveDate,
        };

        const salesforceAssociations: SalesforceAssociation[] =
            isActivityAssociated
                ? [
                      {
                          salesforceAccountId: sfAccountId,
                          salesforceObjectId: resolveSfObjectId(sfObject),
                          salesforceObjectType: resolveSfObjectType(sfObject),
                      },
                  ]
                : [];

        // Phase 3: Write and Read ONLY via PLM (through AdsBlink BFF)
        if (flags[FeatureFlags.PLM_WRITE_AND_READ_PLM]) {
            // Write ALL associations (Activity + Account/Opportunity) via PLM through AdsBlink BFF
            const allAssociations: SalesforceAssociation[] =
                isActivityAssociated
                    ? salesforceAssociations
                    : [
                          {
                              salesforceAccountId: sfAccountId,
                              salesforceObjectId: accountOpportunityId,
                              salesforceObjectType:
                                  SalesforceObjectType.OPPORTUNITY,
                          },
                      ];

            await createAssociationViaBlinkAction.execute(
                contentMetadata,
                allAssociations,
            );
        }
        // Phase 1 & 2: Write to BOTH Theme Service AND PLM (through AdsBlink BFF)
        else if (
            flags[FeatureFlags.PLM_WRITE_BOTH_READ_ACC] ||
            flags[FeatureFlags.PLM_WRITE_BOTH_READ_PLM]
        ) {
            // Write Activity associations via PLM through AdsBlink BFF
            if (isActivityAssociated) {
                await createAssociationViaBlinkAction.execute(
                    contentMetadata,
                    salesforceAssociations,
                );
            }
            // Also write Account/Opportunity associations via PLM through AdsBlink BFF
            else {
                const opportunityAssociations: SalesforceAssociation[] = [
                    {
                        salesforceAccountId: sfAccountId,
                        salesforceObjectId: accountOpportunityId,
                        salesforceObjectType: SalesforceObjectType.OPPORTUNITY,
                    },
                ];
                await createAssociationViaBlinkAction.execute(
                    contentMetadata,
                    opportunityAssociations,
                );
            }
            // Note: Account/Opportunity already written to Theme Service via putContentDetailsAction above
        }
        // Legacy: Write Activity associations via direct PLM calls
        else {
            await createActivityAssociationAction.execute(
                contentMetadata,
                salesforceAssociations,
            );
            // Note: Account/Opportunity already written to Theme Service via putContentDetailsAction above
        }

        upsertNotification(
            "Insight deck successfully uploaded",
            NotificationType.SUCCESS,
            insightTitle,
        );

        resetInsightDetails();

        return {} as PitchFileUploadActionResponse;
    }, {});
}
